<template>
  <ViewContainer>
    <v-row class="mt-1 mb-1">
      <v-col>
        <v-btn color="blue" plain class="pa-0 ma-0" @click="handleBack">
          <v-icon size="16" class="me-2">mdi-arrow-left</v-icon>

          {{ backText }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0 mt-2 mb-4">
        <div class="d-flex align-center">
          <p class="ma-0 pa-0 h6 blue-grey--text me-3">
            {{ `${$t('common.organization_structure')} / `
            }}<span class="blue-grey--text text--darken-3">{{
              tabs[activeTab].text
            }}</span>
          </p>
          <CreateButton small @click="handleCreateClick" :tourId="tourId" />
        </div>
      </v-col>
    </v-row>
    <v-card class="pe-6 mt-6">
      <v-row class="mb-0">
        <v-col cols="12" sm="12" md="7" lg="6" xl="5">
          <v-tabs
            grow
            v-model="activeTab"
            slider-size="4"
            mobile-breakpoint="100"
            slider-color="blue"
            :show-arrows="false"
          >
            <v-tab
              active-class="blue-grey--text text--darken-3"
              class="blue-grey--text"
              v-for="tab in tabs"
              :key="tab.value"
              :data-tourid="tab.tourId"
              :disabled="tab.disabled"
              >{{ tab.text }}</v-tab
            >
          </v-tabs>
        </v-col>

        <v-col
          cols="7"
          sm="7"
          md="3"
          lg="4"
          xl="4"
          offset="5"
          offset-sm="5"
          offset-md="2"
          offset-lg="2"
          offset-xl="3"
          class="d-flex justify-end"
        >
          <SearchInput
            class="mt-4 justify-end"
            :value="searchTerm"
            @change="handleSearch"
          />
        </v-col>
      </v-row>
      <v-tabs-items v-model="activeTab" id="tabs">
        <v-tab-item>
          <SitesTab
            :sites="sites"
            :loading="loading"
            :organization="organization"
            :totalSites="totalSites"
            :groups="currentOrganizationSiteGroups"
            :organizationId="organizationId"
            @getSites="getSites"
            @getSiteGroups="getSiteGroups"
            @createGroup="showCreateSitesGroupModal = true"
            @manageGroups="goToSiteGroupsTab"
            @selectSites="handleSelectSites"
            @cleanSearchTerm="searchTerm = ''"
          />
        </v-tab-item>
        <v-tab-item>
          <SiteGroupsTab
            :totalSiteGroups="totalSiteGroups"
            :organizationId="organizationId"
            :siteGroups="siteGroups"
            :loading="loading"
            @getSiteGroups="getSiteGroups"
            @getSites="getSites"
            @cleanSearchTerm="searchTerm = ''"
          />
        </v-tab-item>
        <v-tab-item>
          <TagsTab
            :organizationId="organizationId"
            :tags="tags"
            :loading="loading"
            :totalTags="totalTags"
            :groups="currentOrganizationTagGroups"
            @getTags="getTags"
            @getTagGroups="getTagGroups"
            @createGroup="showCreateTagsGroupModal = true"
            @manageGroups="goToTagGroupsTab"
            @selectTags="handleSelectTags"
            @cleanSearchTerm="searchTerm = ''"
          />
        </v-tab-item>
        <v-tab-item>
          <TagGroupsTab
            :organizationId="organizationId"
            :totalTagGroups="totalTagGroups"
            :tagGroups="tagGroups"
            :loading="loading"
            @getTagGroups="getTagGroups"
            @getTags="getTags"
            @cleanSearchTerm="searchTerm = ''"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <CreateSiteModal
      :organization="organization"
      :loading="loading"
      :showDialog="showCreateSiteModal"
      @cancel="showCreateSiteModal = false"
      @createSite="createSite"
    />
    <CreateModal
      :loading="loading"
      :showDialog="showCreateTagModal"
      :title="$t('organization_structure.create_tag')"
      @close="showCreateTagModal = false"
      @save="createTag"
    />
    <CreateModal
      :loading="loading"
      :showDialog="showCreateSitesGroupModal"
      :title="$t('organization_structure.create_site_group')"
      @close="showCreateSitesGroupModal = false"
      @save="createSitesGroup"
    />
    <CreateModal
      :loading="loading"
      :showDialog="showCreateTagsGroupModal"
      :title="$t('organization_structure.create_tag_group')"
      @close="showCreateTagsGroupModal = false"
      @save="createTagsGroup"
    />
  </ViewContainer>
</template>
<script>
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'

import api from '@/api'
import mixin from '@/mixins/mixin'

import SitesTab from './Tabs/SitesTab/SitesTab.vue'
import TagsTab from './Tabs/TagsTab/TagsTab.vue'
import SiteGroupsTab from './Tabs/SitesGroupsTab/SitesGroupsTab.vue'
import TagGroupsTab from './Tabs/TagsGroupsTab/TagsGroupsTab.vue'
import CreateModal from './Shared/CreateModal.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import CreateButton from '@/components/UI/CreateButton.vue'

import CreateSiteModal from '@/components/Admin/Sites/CreateSiteDialog.vue'

import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'

const TABS = { SITES: 0, SITES_GROUPS: 1, TAGS: 2, TAGS_GROUPS: 3 }
const DEFAULT_SIZE = 100
const DEFAULT_SORT = 'createdAt'
const SORT_BY_COINCIDENCE = '_score'

export default {
  name: 'OrganizationStructureView',

  mixins: [mixin],

  components: {
    ViewContainer,
    SitesTab,
    TagsTab,
    TagGroupsTab,
    SiteGroupsTab,
    CreateSiteModal,
    CreateModal,
    SearchInput,
    CreateButton,
  },

  data() {
    return {
      activeTab: TABS.SITES,
      tabs: [
        {
          value: TABS.SITES,
          text: this.$t('common.site_other'),
          tourId: 'tab-sites-strcucture',
        },
        {
          value: TABS.SITES_GROUPS,
          text: this.$t('common.site_group_other'),
          tourId: 'tab-site-groups-structure',
        },
        {
          value: TABS.TAGS,
          text: this.$t('common.tag_other'),
          tourId: 'tab-tags-structure',
        },
        {
          value: TABS.TAGS_GROUPS,
          text: this.$t('common.tag_group_other'),
          tourId: 'tab-tag-groups-structure',
        },
      ],
      sites: [],
      tags: [],
      siteGroups: [],
      tagGroups: [],
      loading: false,
      totalSites: 0,
      totalTags: 0,
      totalSiteGroups: 0,
      totalTagGroups: 0,
      showCreateSiteModal: false,
      showCreateSitesGroupModal: false,
      showCreateTagModal: false,
      showCreateTagsGroupModal: false,
      allTagGroups: [],
      allSiteGroups: [],
      previousRoute: null,
      searchTerm: '',
      organization: {},
      selectedTagIds: [],
      selectedSiteIds: [],
    }
  },

  watch: {
    activeTab(newValue, oldValue) {
      if (oldValue !== newValue) {
        this.searchTerm = ''
        return
      }
    },
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousRoute = from
    })
  },

  mounted() {
    this.getOrganization()
    if (!this.isOwnOrganization) {
      this.getAllGroups()
    }
  },

  computed: {
    ...mapGetters([
      'account',
      'organizationSiteGroups',
      'organizationTagGroups',
    ]),

    isOwnOrganization() {
      return this.account?.organization?._id === this.organizationId
    },

    currentOrganizationSiteGroups() {
      return this.isOwnOrganization
        ? this.organizationSiteGroups
        : this.allSiteGroups
    },

    currentOrganizationTagGroups() {
      return this.isOwnOrganization
        ? this.organizationTagGroups
        : this.allTagGroups
    },

    tourId() {
      switch (this.activeTab) {
        case TABS.SITES:
          return 'createButton-button-create-sites-structure'
        case TABS.SITES_GROUPS:
          return 'createButton-button-create-site-groups-structure'
        case TABS.TAGS:
          return 'createButton-button-create-tags-structure'
        case TABS.TAGS_GROUPS:
          return 'createButton-button-create-tag-groups-structure'
        default:
          return ''
      }
    },

    backText() {
      const validRoutes = [
        'organization_settings',
        'organizations',
        'site_settings',
      ]

      const routeName = _.snakeCase(this.previousRoute?.name)

      if (validRoutes.includes(routeName)) {
        return this.$t(`organization_structure.route.${routeName}`)
      }

      return this.$t('organization_structure.route.back')
    },

    organizationId() {
      return this.$route?.params?.organizationId
    },
  },

  methods: {
    ...mapActions(['getOrganizationStructure']),

    handleBack() {
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo({
          name: 'organization-settings',
          params: { organizationId: this.organizationId },
        })
      }
    },

    async getOrganization() {
      try {
        this.loading = true
        const response = await api.organization.getById(this.organizationId)
        this.organization = response.data ?? {}
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    handleSelectTags(selectedTagIds) {
      const selectedIds = selectedTagIds
        .filter(item => item.isEnabled)
        .map(item => item.id)
      this.selectedTagIds = selectedIds
    },

    handleSelectSites(selectedSiteIds) {
      const selectedIds = selectedSiteIds
        .filter(item => item.isEnabled)
        .map(item => item.id)
      this.selectedSiteIds = selectedIds
    },

    goToTagGroupsTab() {
      this.activeTab = TABS.TAGS_GROUPS
    },

    goToSiteGroupsTab() {
      this.activeTab = TABS.SITES_GROUPS
    },

    handleSearch(terms) {
      this.searchTerm = terms
      switch (this.activeTab) {
        case TABS.SITES:
          return this.getSites()
        case TABS.SITES_GROUPS:
          return this.getSiteGroups()
        case TABS.TAGS:
          return this.getTags()
        case TABS.TAGS_GROUPS:
          return this.getTagGroups()
      }
    },

    async createSitesGroup(sitesGroup) {
      try {
        this.loading = true
        const siteResponse = await api.sitegroups.create(
          this.organizationId,
          sitesGroup
        )
        const sites = _.uniq([
          ...siteResponse.data.sites,
          ...this.selectedSiteIds,
        ])

        if (sites?.length > this.$constants.maxTagsPerGroup)
          return this.showError(
            this.$t('organization_structure.cannot_add_more_than_100_sites')
          )

        await api.sitegroups.editSites(
          this.organizationId,
          siteResponse?.data?.id,
          {
            sites,
          }
        )
        this.getSites()
        _.delay(this.getSiteGroups, 500)

        this.showCreateSitesGroupModal = false
        if (this.isOwnOrganization) {
          this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.showSuccess(
          this.$t('organization_structure.site_group_created_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async createTagsGroup(tagsGroup) {
      try {
        this.loading = true
        const groupResponse = await api.tagGroups.create(
          this.organizationId,
          tagsGroup
        )
        const tags = _.uniq([
          ...groupResponse?.data?.tags,
          ...this.selectedTagIds,
        ])

        if (tags?.length > this.$constants.maxTagsPerGroup)
          return this.showError(
            this.$t('organization_structure.cannot_add_more_than_100_tags')
          )

        await api.tagGroups.addTags(
          this.organizationId,
          groupResponse?.data?.id,
          {
            tags,
          }
        )
        this.getTags()

        this.showCreateTagsGroupModal = false
        if (this.isOwnOrganization) {
          this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
        this.showSuccess(
          this.$t('organization_structure.tags_group_created_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        _.delay(this.getTagGroups, 500)
        this.loading = false
      }
    },

    handleCreateClick() {
      switch (this.activeTab) {
        case TABS.SITES:
          this.showCreateSiteModal = true
          return
        case TABS.SITES_GROUPS:
          this.showCreateSitesGroupModal = true
          return
        case TABS.TAGS:
          this.showCreateTagModal = true
          return
        case TABS.TAGS_GROUPS:
          this.showCreateTagsGroupModal = true
          return
        default:
          return
      }
    },

    async createSite(site) {
      this.loading = true
      try {
        await api.site.createOrganizationSite(this.organizationId, site)
        if (this.isOwnOrganization) {
          this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
      } catch (err) {
        if (err.response.data.errorCode === 'SI001') {
          return this.showError(
            this.$t('organization_structure.site_limit_reached')
          )
        }
        this.handleRequestError(err)
      } finally {
        this.loading = false
        this.showCreateSiteModal = false
        _.delay(this.getSites, 500)
      }
    },

    async createTag(tag) {
      this.loading = true
      try {
        await api.tag.create(this.organizationId, tag)
        if (this.isOwnOrganization) {
          this.getOrganizationStructure({
            organizationId: this.organizationId,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
        _.delay(this.getTags, 500)
        this.showCreateTagModal = false
      }
    },

    async getSites(options = {}) {
      this.loading = true
      const terms = this.searchTerm?.toLowerCase() ?? ''

      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT, order: 'desc' }

      const itemsPerPage = options?.itemsPerPage ?? DEFAULT_SIZE
      const page = options?.page ?? 1

      try {
        const response = await api.site.getStreamSites(this.organizationId, {
          limit: itemsPerPage,
          sort,
          terms,
          from: itemsPerPage * (page - 1),
          relations: ['groups'],
        })

        this.sites = response?.data?.results.map(item => item.site) ?? []

        this.totalSites = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getSiteGroups(options = {}) {
      this.loading = true
      const terms = this.searchTerm?.toLowerCase() ?? ''

      const sort = {
        by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT,
        order: 'desc',
      }

      if (options?.sortBy?.length) {
        sort.by = options.sortBy
        sort.order = options.sortDesc ? 'desc' : 'asc'
      }
      const itemsPerPage = options?.itemsPerPage ?? DEFAULT_SIZE
      const page = options?.page ?? 1

      try {
        const response = await api.sitegroups.getStreamSiteGroups(
          this.organizationId,
          {
            limit: itemsPerPage,
            sort,
            terms,
            from: itemsPerPage * (page - 1),
          }
        )

        this.siteGroups =
          response?.data?.results?.map(item => item.siteGroup) ?? []

        this.totalSiteGroups = response?.data?.totalRecordsInData ?? 0

        if (!this.isOwnOrganization) {
          this.getAllGroups()
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getTags(options = {}) {
      this.loading = true

      const terms = this.searchTerm?.toLowerCase() ?? ''
      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT, order: 'desc' }

      const itemsPerPage = options?.itemsPerPage ?? DEFAULT_SIZE
      const page = options?.page ?? 1

      try {
        const response = await api.tag.getStreamTags(this.organizationId, {
          limit: itemsPerPage ?? DEFAULT_SIZE,
          sort,
          terms,
          from: itemsPerPage * (page - 1),
        })

        this.tags = response?.data?.results.map(item => item.tag) ?? []

        this.totalTags = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getTagGroups(options = {}) {
      this.loading = true
      const terms = this.searchTerm?.toLowerCase() ?? ''

      const sort = options?.sortBy
        ? { by: options.sortBy, order: options.sortDesc ? 'desc' : 'asc' }
        : { by: terms ? SORT_BY_COINCIDENCE : DEFAULT_SORT, order: 'desc' }

      try {
        const response = await api.tagGroups.getStreamTagGroups(
          this.organizationId,
          {
            limit: options?.itemsPerPage ?? DEFAULT_SIZE,
            page: options?.page ?? 1,
            sort,
            terms,
          }
        )

        this.tagGroups =
          response?.data?.results.map(result => result.tagGroup) ?? []

        this.totalTagGroups = response?.data?.totalRecordsInData ?? 0

        if (!this.isOwnOrganization) {
          this.getAllGroups()
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getAllGroups() {
      this.loading = true

      try {
        const response = await api.organization.getOrganizationStructure(
          this.organizationId
        )

        this.allSiteGroups = response.data.siteGroups
        this.allTagGroups = response.data.tagGroups
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
